import { useEffect, useRef, useState } from 'react';
import {
  getTournamentById,
  getTournamentFixture,
} from '@/api/tournament/tournamentApi';
import type { Fixture, Tournament } from '@/types/tournament.model';

export type TVTournamentSnapshot = {
  tournament: Tournament;
  fixture: Fixture | null;
};

type State = {
  data: TVTournamentSnapshot[];
  isInitialLoading: boolean;
  error: string | null;
};

const POLL_INTERVAL_MS = 2000;

export const useTVTournamentData = (
  tournamentIds: string[],
): State => {
  const [state, setState] = useState<State>({
    data: [],
    isInitialLoading: true,
    error: null,
  });

  const inFlightRef = useRef(false);
  const idsKey = tournamentIds.join(',');

  useEffect(() => {
    if (tournamentIds.length === 0) {
      setState({ data: [], isInitialLoading: false, error: null });
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchOnce = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const results = await Promise.all(
          tournamentIds.map(async (id) => {
            const [tournament, fixtureResult] = await Promise.all([
              getTournamentById(id),
              getTournamentFixture(id).catch(() => null),
            ]);
            return {
              tournament,
              fixture: (fixtureResult as Fixture | null) ?? null,
            };
          }),
        );
        if (cancelled) return;
        setState({ data: results, isInitialLoading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching TV mode data', err);
        setState((prev) => ({
          data: prev.data,
          isInitialLoading: prev.isInitialLoading && prev.data.length === 0,
          error: 'No se pudo actualizar la información en vivo',
        }));
      } finally {
        inFlightRef.current = false;
      }
    };

    const schedule = () => {
      timer = setTimeout(async () => {
        await fetchOnce();
        if (!cancelled) schedule();
      }, POLL_INTERVAL_MS);
    };

    fetchOnce().then(() => {
      if (!cancelled) schedule();
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // idsKey reflects the array contents — avoids restart on referentially new but equal arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return state;
};
